import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
    AssistantUsage,
    ConversationContextParts,
    ConversationContextSize,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { Minimize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { panelClasses } from "./assistantClasses";

// Gauge thresholds, as a share of the conversation's maximum size: orange from half, red from 80 %.
const CONTEXT_ORANGE_RATIO = 0.5;
const CONTEXT_RED_RATIO = 0.8;

type ContextLevel = "green" | "orange" | "red";

const contextLevel = (size: ConversationContextSize): ContextLevel => {
    const ratio = size.used / size.max;
    if (ratio >= CONTEXT_RED_RATIO) return "red";
    if (ratio >= CONTEXT_ORANGE_RATIO) return "orange";

    return "green";
};

/** Share of the conversation's maximum size, in whole percent (at most 100). */
const percentOf = (tokens: number, max: number) =>
    Math.min(100, Math.round((tokens / max) * 100));

// Dark shades: the ring keeps a 3:1 contrast on white.
const RING_COLOR: Record<ContextLevel, string> = {
    green: "stroke-success-dark",
    orange: "stroke-warning-dark",
    red: "stroke-error-dark",
};
// Out of green, the trigger itself takes the colour.
const TRIGGER_COLOR: Record<ContextLevel, string> = {
    green: "text-neutral-dark hover:bg-primary-lightest",
    orange: "bg-warning-lightest text-warning-darkest",
    red: "bg-error-lightest text-error-darkest",
};

// The panel, in Anthropic's colours (shared with the memory), and its four brand accents for the parts.
const PANEL = panelClasses;
// Without the breakdown, the bar takes the level, in the same tones.
const BAR_COLOR: Record<ContextLevel, string> = {
    green: "bg-gauge-green",
    orange: "bg-gauge-orange",
    red: "bg-gauge-red",
};

// What fills the conversation (estimated by the Api-Ai), in this order on the bar.
const PARTS: (keyof ConversationContextParts)[] = [
    "instructions",
    "exchanges",
    "reads",
    "reasoning",
];
const PART_COLOR: Record<keyof ConversationContextParts, string> = {
    instructions: "bg-gauge-grey",
    exchanges: "bg-gauge-orange",
    reads: "bg-gauge-blue",
    reasoning: "bg-gauge-green",
};

// Ring drawn in a 22×22 box.
const RING_RADIUS = 9;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * The bar cut by what fills the conversation, then a line per part: an estimate (the provider only
 * gives the total), written « environ ».
 */
const ContextParts = ({
    parts,
    max,
}: {
    parts: ConversationContextParts;
    max: number;
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-2.5">
            <div
                className={cn(
                    "flex h-1.5 overflow-hidden rounded-full",
                    PANEL.track,
                )}
            >
                {PARTS.map((part) => (
                    <div
                        key={part}
                        className={cn("h-full", PART_COLOR[part])}
                        data-part={part}
                        style={{ width: `${(parts[part] / max) * 100}%` }}
                    />
                ))}
            </div>
            <ul className="flex flex-col gap-1.5 text-[13px]">
                {PARTS.filter((part) => parts[part] > 0).map((part) => (
                    <li key={part} className="flex items-center gap-2">
                        <span
                            className={cn(
                                "h-2 w-2 flex-none rounded-[2px]",
                                PART_COLOR[part],
                            )}
                        />
                        <span className={cn("min-w-0 flex-1", PANEL.ink)}>
                            {t(`vistassistant.contextGauge.parts.${part}`)}
                        </span>
                        <span className={cn("tabular-nums", PANEL.muted)}>
                            {t("vistassistant.contextGauge.partApprox", {
                                percent: percentOf(parts[part], max),
                            })}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * What the assistant has consumed this month, against the month's mark: a bar and a percentage, no
 * token count (the mark is a cost reference: nothing is blocked beyond it). Without a mark from the
 * backend, nothing.
 */
const MonthUsage = ({ usage }: { usage: AssistantUsage }) => {
    const { t } = useTranslation();
    const max = usage.monthMaxTokens;
    if (!max) return null;

    const percent = percentOf(usage.monthTokens, max);
    const level = contextLevel({ used: usage.monthTokens, max });
    // Something was consumed, but too little to make one percent: « < 1 % », never « 0 % ».
    const isUnderOne = percent === 0 && usage.monthTokens > 0;

    return (
        <div
            className={cn("flex flex-col gap-2.5 border-t pt-3", PANEL.border)}
            data-testid="context-gauge-usage"
        >
            <div className="flex items-baseline justify-between gap-2">
                <p className={cn("text-[13px] font-medium", PANEL.ink)}>
                    {t("vistassistant.contextGauge.usage.title")}
                </p>
                <p className={cn("text-[13px] tabular-nums", PANEL.muted)}>
                    {isUnderOne
                        ? t("vistassistant.contextGauge.usage.underOne")
                        : t("vistassistant.contextGauge.percent", { percent })}
                </p>
            </div>
            <div
                aria-label={t("vistassistant.contextGauge.usage.monthLabel", {
                    percent,
                })}
                className={cn(
                    "h-1.5 overflow-hidden rounded-full",
                    PANEL.track,
                )}
                data-level={level}
                role="img"
            >
                <div
                    className={cn("h-full rounded-full", BAR_COLOR[level])}
                    style={{ width: `${isUnderOne ? 1 : percent}%` }}
                />
            </div>
        </div>
    );
};

type ContextGaugeProps = {
    size?: ConversationContextSize;
    usage?: AssistantUsage;
    canCompact: boolean;
    isCompacting: boolean;
    onCompact: () => void;
    onNewConversation: () => void;
};

/**
 * Size of the conversation the assistant rereads on every question (step-based contract), in the
 * input bar: a ring and the percentage, in three colours. A click opens what it means, « Compacter »
 * and « Nouvelle conversation », and the share of the month's mark the assistant has consumed, once
 * the SDK has read it. Unknown size (before the first turn): nothing.
 */
const ContextGauge = ({
    size,
    usage,
    canCompact,
    isCompacting,
    onCompact,
    onNewConversation,
}: ContextGaugeProps) => {
    const { t, i18n } = useTranslation();
    if (!size) return null;

    const percent = percentOf(size.used, size.max);
    const level = contextLevel(size);
    const label = t("vistassistant.contextGauge.label", { percent });
    // The exact count, as the provider measured it on the last call (the parts below are estimates).
    const count = new Intl.NumberFormat(i18n.language);
    const tokens = t("vistassistant.contextGauge.tokens", {
        used: count.format(size.used),
        max: count.format(size.max),
    });

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    aria-label={label}
                    className={cn(
                        "flex h-7 items-center gap-1 rounded-md px-1.5 text-xs font-semibold tabular-nums transition-colors",
                        TRIGGER_COLOR[level],
                    )}
                    data-level={level}
                    type="button"
                >
                    <svg
                        aria-hidden
                        className="-rotate-90"
                        height={16}
                        viewBox="0 0 22 22"
                        width={16}
                    >
                        <circle
                            className="fill-none stroke-primary-lighter"
                            cx={11}
                            cy={11}
                            r={RING_RADIUS}
                            strokeWidth={3}
                        />
                        <circle
                            className={cn(
                                "fill-none transition-all duration-500",
                                RING_COLOR[level],
                            )}
                            cx={11}
                            cy={11}
                            r={RING_RADIUS}
                            strokeDasharray={RING_LENGTH}
                            strokeDashoffset={RING_LENGTH * (1 - percent / 100)}
                            strokeLinecap="round"
                            strokeWidth={3}
                        />
                    </svg>
                    {t("vistassistant.contextGauge.percent", { percent })}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className={cn(
                    "flex w-[300px] flex-col gap-3 rounded-xl border p-3.5 shadow-[0_4px_16px_rgba(20,20,19,0.06)]",
                    PANEL.surface,
                    PANEL.border,
                )}
                side="top"
            >
                <div className="flex items-baseline justify-between gap-2">
                    <p className={cn("text-[13px] font-medium", PANEL.ink)}>
                        {t("vistassistant.contextGauge.title")}
                    </p>
                    <p
                        className={cn("text-[13px] tabular-nums", PANEL.muted)}
                        data-testid="context-gauge-tokens"
                    >
                        {tokens}
                    </p>
                </div>
                {size.parts ? (
                    <ContextParts max={size.max} parts={size.parts} />
                ) : (
                    <div
                        className={cn(
                            "h-1.5 overflow-hidden rounded-full",
                            PANEL.track,
                        )}
                    >
                        <div
                            className={cn(
                                "h-full rounded-full",
                                BAR_COLOR[level],
                            )}
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                )}
                {/* Only when the conversation gets long: what to do about it. */}
                {level !== "green" && (
                    <p className={cn("text-[13px] leading-normal", PANEL.ink)}>
                        {t(`vistassistant.contextGauge.${level}`)}
                    </p>
                )}
                {usage && <MonthUsage usage={usage} />}
                <div className="flex flex-wrap items-center gap-1.5">
                    {canCompact && (
                        <Button
                            className={cn(
                                "h-8 rounded-lg px-3 text-[13px] font-medium [&_svg]:size-3.5",
                                level === "green"
                                    ? cn(
                                          "border bg-white",
                                          PANEL.border,
                                          PANEL.ink,
                                          PANEL.hover,
                                      )
                                    : "bg-gauge-ink text-gauge-surface hover:bg-gauge-inkHover",
                            )}
                            disabled={isCompacting}
                            onClick={onCompact}
                            type="button"
                        >
                            <Minimize2 />
                            {t("vistassistant.compactConversation")}
                        </Button>
                    )}
                    <Button
                        className={cn(
                            "h-8 rounded-lg px-3 text-[13px] font-medium",
                            PANEL.ink,
                            PANEL.hover,
                        )}
                        onClick={onNewConversation}
                        type="button"
                        variant="ghost"
                    >
                        {t("vistassistant.newConversation")}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default ContextGauge;
